import { useCallback, useEffect, useState } from "react"
import { Application } from "@core/Application"
import { KioskRecordEvidence } from "@services/DashboardService"
import { Logger } from "@shared/utils/Logger"

export interface KioskEvidenceRequest {
    recordID: string
    collaboratorName: string
    /** "Ingreso" / "Egreso" — the row already knows which half of the pair was clicked */
    recordTypeLabel: string
    /** already formatted for display by the table */
    timeLabel: string
    /** the table flagged this fichada's photo as a possible impersonation (anti-spoof check) */
    spoofSuspected?: boolean
}

/**
 * Loads the two photos behind a kiosk fichada.
 *
 * Each image is tracked separately on purpose: the backend can legitimately return one
 * URL and not the other, and even a returned URL can 404 once the 90-day lifecycle rule
 * has expired the object. A missing photo is an expected state to render, not an error.
 */
export function useKioskEvidenceModalViewModel(request: Pick<KioskEvidenceRequest, "recordID"> | null) {
    const recordID = request?.recordID
    const [evidence, setEvidence] = useState<KioskRecordEvidence | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [evidenceImageFailed, setEvidenceImageFailed] = useState(false)
    const [referenceImageFailed, setReferenceImageFailed] = useState(false)

    useEffect(() => {
        if (!recordID) return

        let cancelled = false
        setLoading(true)
        setError(null)
        setEvidence(null)
        setEvidenceImageFailed(false)
        setReferenceImageFailed(false)

        Application.shared().coreDependencies.dashboardService
            .getKioskRecordEvidence(recordID)
            .then((result) => {
                if (cancelled) return
                setEvidence(result)
            })
            .catch((caught) => {
                if (cancelled) return
                Logger.logObject("kiosk evidence fetch failed", caught)
                setError("No pudimos cargar las fotos de esta fichada.".localized())
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => { cancelled = true }
    }, [recordID])

    const onEvidenceImageError = useCallback(() => setEvidenceImageFailed(true), [])
    const onReferenceImageError = useCallback(() => setReferenceImageFailed(true), [])

    // The URL is signed without checking the object, so "we got a URL" and "there is an
    // image" are different questions; only the load result answers the second one.
    const evidenceImageURL = evidenceImageFailed ? undefined : evidence?.evidenceImageURL
    const referenceImageURL = referenceImageFailed ? undefined : evidence?.referenceImageURL

    return {
        loading,
        error,
        evidence,
        evidenceImageURL,
        referenceImageURL,
        onEvidenceImageError,
        onReferenceImageError
    }
}
